<template>
    <section>
        <!-- Item List -->
        <MultiStepFilterListItem v-for="(item, index) in listItems"
            class="sm-b-s"
            :key="item.value"
            :item="item"
            :min="min"
            :selection-count="selectedItems.length"
            :lockedItems="lockedItems"
            :allowSwap="allowSwap"
            :lockedTitle="lockedTitle"
            @onRemove="onRemove(item)"
            @onSwapItem="onSwapItem($event, index)" />
        <a class="sm-t-m sm-b-s te-link show-more-btn" @click="showAll()" v-if="canMore">{{ showMoreLbl }}</a>
        <!-- Add New Item -->
        <ButtonIcon class="el--s-m"
            icon="plus"
            @click="onAddItem"
            :class="[!addingDisabled ? 'el--t-primary' : '']"
            :disabled="addingDisabled" />
    </section>
</template>
<script>
import { guuid, isArray } from '@aretheyhappy/utils';
import MultiStepFilterPopover from '../multi-step-filter-popover/MultiStepFilterPopover.vue';
import ButtonIcon from '../../atoms/ButtonIcon.vue';
import MultiStepFilterListItem from './MultiStepFilterListItem.vue';

export default {
    name: 'mead-multi-step-filter-list',
    components: {MultiStepFilterListItem, ButtonIcon},
    props: {
        /**
         * Title/header text shown in the item-selection popover opened when adding or swapping
         * an item; forwarded as the `title` prop to MultiStepFilterPopover.
         */
        title: {type: String, default: 'mead.options' },
        /**
         * The full hierarchical list of selectable items, used to look up the full item for each
         * selected value and forwarded to the popover opened when adding or swapping items.
         */
        items: {type: Array, default: () => []},
        /**
         * The values currently selected, rendered as rows in the list (in the order they resolve
         * against items).
         */
        selectedItems: {type: Array, default: () => []},
        /**
         * Maximum number of items that may be selected. The add button is disabled once this
         * limit is reached; 0 means unlimited.
         */
        max: {type: Number, default: 0},
        /**
         * Minimum number of items that must remain selected; forwarded to each row so it can
         * disable removal once the selection would drop below this count.
         */
        min: {type: Number, default: 0},
        /**
         * Whether group items are included as selectable entries in the selection popover.
         */
        includeGroups: {type: Boolean, default: true},
        /**
         * Whether an entire group can be selected as a single value in the selection popover.
         */
        allowGroupSelection: {type: Boolean, default: false},
        /**
         * Whether to show badges with selected-descendant counts on group items in the selection
         * popover.
         */
        showBadgeCounts: {type: Boolean, default: true},
        /**
         * The selection mode used when opening the "add item" popover (e.g. single vs. multi
         * selection), controlling whether multiple items can be added before the popover closes.
         */
        mode: {type: String, default: () => MeadCommon.MUTLI_STEP_FILTER_MODES.SINGLE},
        /**
         * Whether partially-selected groups should be kept as-is (rather than fully expanded)
         * when passed through to the selection popover.
         */
        keepPartialGroups: {type: Boolean, default: false},
        /**
         * Whether selecting a group in the selection popover should only include the group value
         * itself rather than its children's values.
         */
        isolateSelection: {type: Boolean, default: false},
        /**
         * Height of the selection popover's scrollable item list.
         */
        uiHeight: {type: [String, Number], default: '40'},

        /**
         * Items that cannot be removed from the list; forwarded to each row so it can hide or
         * disable its remove control for those items.
         */
        lockedItems: {type: Array, default: () => []},
        /**
         * Tooltip to show on locked items.
         */
        lockedTitle: {type: String, default: ''},
        /**
         * Allow items to be swapped (show a chevron on each row and allow clicking it to open the
         * swap popover).
         */
        allowSwap: {type: Boolean, default: true},
        /**
         * Maximum number of selected items shown before the rest are collapsed behind a
         * "show more" link; 0 shows all items.
         */
        maxShown: {type: Number, default: 0},
    },
    data: function() {
        return {
            uid: guuid(),
            swap_index: undefined,
            show_all: false,
        }
    },
    methods: {
        onRemove: function (item) {
            this.$emit('onRemove', item.value);
        },
        onAdd: function (selection) {
            if (selection.value.length > 0) {
                if (this.swap_index !== undefined) {
                    this.$emit('onSwap', this.swap_index, selection.value[0])
                    // Always close in swap mode
                    MeadCommon.PopoverService.close();
                } else {
                    for (const item of selection.value) {
                        this.$emit('onAdd', item);
                    }
                    // Only close popover in SINGLE mode
                    // In MULTI mode, let user select multiple items and close manually
                    if (this.mode !== MeadCommon.MUTLI_STEP_FILTER_MODES.MULTI) {
                        MeadCommon.PopoverService.close();
                    }
                }
            }
        },
        onCheck: function (items) {
            // In MULTI mode, items is the entire updated selection array
            // We need to sync it with the current selectedItems

            // Capture current state to avoid reactivity issues during processing
            const previousSelection = [...this.selectedItems];

            // Find items that were removed (in previousSelection but not in items)
            for (const currentValue of previousSelection) {
                if (!items.includes(currentValue)) {
                    this.$emit('onRemove', currentValue);
                }
            }

            // Find items that were added (in items but not in previousSelection)
            for (const newValue of items) {
                if (!previousSelection.includes(newValue)) {
                    this.$emit('onAdd', newValue);
                }
            }
        },
        onSwapItem: function (event, index) {
            this.swap_index = index;
            this.openPopover(event, MeadCommon.MUTLI_STEP_FILTER_MODES.SINGLE);
        },
        onAddItem: function(event) {
            this.swap_index = undefined;
            this.openPopover(event, this.mode);
        },
        openPopover: function (event, mode) {
            event.preventDefault();
            event.stopPropagation();

            const config = new MeadCommon.PopoverConfig()
                .setComponent(MultiStepFilterPopover)
                .setMovable(true)
                .setNoPad(true)
                .setEvent(event)
                .setIndex(1)
                .setProps({
                    title: this.title,
                    pubkey: this.uid,
                    items: this.items,
                    selectedItems: mode === MeadCommon.MUTLI_STEP_FILTER_MODES.MULTI ? [...this.selectedItems] : [],
                    restrictedItems: mode === MeadCommon.MUTLI_STEP_FILTER_MODES.SINGLE ? [...this.selectedItems] : [],
                    max: this.max,
                    min: this.min,
                    includeGroups: this.includeGroups,
                    allowGroupSelection: this.allowGroupSelection,
                    mode,
                    showBadgeCounts: this.showBadgeCounts,
                    keepPartialGroups: this.keepPartialGroups,
                    isolateSelection: this.isolateSelection,
                    uiHeight: this.uiHeight,
                    startingItem: this.swap_index !== undefined ? this.listItems[this.swap_index].value : undefined
                });
            MeadCommon.PopoverService.open(config);
        },
        findItemWithValue: function (list, value, items=[]) {
            for (const item of list) {
                if (item.value === value) items.push(item);
                if (item.isGroup && items.length === 0) {
                    this.findItemWithValue(item.children, value, items);
                }
            }
        },
        showAll: function () {
            this.show_all = !this.show_all;
        },
    },
    computed: {
        listItems: function () {
            const items = [];
            for (const value of this.selectedItems) {
                let found = [];
                this.findItemWithValue(this.items, value, found);
                if (found.length === 1) {
                    items.push(found[0]);
                }
            }
            // Limit to this.maxShown items unless show_all is true
            if (this.maxShown > 0) {
                if (!this.show_all && items.length > this.maxShown) {
                    return items.slice(0, this.maxShown);
                }
            }

            return items;
        },
        addingDisabled: function () {
            return this.selectedItems.length >= this.max && this.max !== 0;
        },
        showMoreLbl: function () {
            if (this.show_all) return MeadCommon.I18N.go('general.show_less');

            const totalItems = this.selectedItems.length;
            return MeadCommon.I18N.go('general.show_x_more', {amt: totalItems - this.maxShown});
        },
        canMore: function () {
            return isArray(this.selectedItems) && this.maxShown > 0 && this.selectedItems.length > this.maxShown;
        },
    },
    created() {
        MeadCommon.Flash.subscribe(this.uid, `${this.uid}-mstp-selected`, this.onAdd);
        MeadCommon.Flash.subscribe(this.uid, `${this.uid}-mstp-checked`, this.onCheck);
    },
    destroyed () {
        MeadCommon.Flash.unsubscribe(this.uid);
    }
}
</script>

<style lang="scss" scoped>
 .show-more-btn {
    display: block;
 }
</style>